import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Box,
  Button,
  Grid,
  IconButton,
  Input,
  Paper,
  Typography
} from "@mui/material";
import Tooltip from "../../../Atoms/Tooltip";
import moment from 'moment';
import React, { useState } from 'react';
import { formatDocument } from "../../../../Utils/formatters";

interface InvoiceDetailProps {
  file: any;
  canEditDueDate?: boolean;
  onAttachPdf: (fileId: string, pdf: File | null) => void;
  onDelete: (fileId: string) => void;
  onUpdateDueDate: (fileId: string, nDup: string, newDueDate: string) => void;
}

const InvoiceDetailComponent: React.FC<InvoiceDetailProps> = ({
  file,
  canEditDueDate,
  onAttachPdf,
  onDelete,
  onUpdateDueDate
}) => {

  const [, setPdfName] = useState<string | null>(null);

  const canEditThisDueDate = (dup) => {
    const isLate = new Date(dup.dVenc) < new Date();
    return canEditDueDate && isLate;
  };

  const hasAnyEditableDueDate = file.dup?.some(d => canEditThisDueDate(d));

  return (
    <Paper elevation={3} sx={{ p: 2, mt: 3, mb: 3 }}>
      <Grid container spacing={2}>

        <Grid item xs={12}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="caption">Arquivo</Typography>
          <Tooltip title="Remover nota">
            <Button
              size="small"
              color="error"
              variant="text"
              onClick={() => onDelete(file.id)}
              startIcon={<DeleteIcon fontSize="small" />}
            >
              Remover
            </Button>
          </Tooltip>
          </Box>

          <Tooltip title={file.fileName || "NF-e.xml"}>
            <Typography noWrap>{file.fileName || 'NF-e.xml'}</Typography>
          </Tooltip>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="caption">Chave de acesso</Typography>
          <Tooltip title={file.id}>
            <Typography noWrap>{file.id}</Typography>
          </Tooltip>
        </Grid>

        <Grid item xs={12} md={3}>
          <Typography variant="caption">Valor Total</Typography>
          <Typography>
            {Number(file.vOrig).toLocaleString("pt-BR", {
              style: "currency",
              currency: "BRL"
            })}
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="caption">Comprador</Typography>
          <Typography>
            {file.dest.cnpj
              ? formatDocument(file.dest.cnpj, "CNPJ_COMPACT")
              : formatDocument(file.dest.cpf, "CPF")}
            {" - "}
            {file.dest.xNome}
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <Button
            sx={{ color: "white", py: 1.2 }}
            size="small"
            variant="contained"
            component="label"
            startIcon={<AttachFileIcon />}
          >
            PDF DA NOTA FISCAL
            <input
              hidden
              type="file"
              accept=".pdf"
              onChange={(e) => {
                const pdfFile = e.target.files?.[0];
                if (pdfFile) {
                  onAttachPdf(file.id, pdfFile);
                  setPdfName(pdfFile.name);
                }
              }}
            />
          </Button>

          {file.pdf && (
            <Box display="flex" alignItems="center" mt={1}>
              <Tooltip title={file.pdf.name}>
                <Typography noWrap flex={1}>
                  {file.pdf.name}
                </Typography>
              </Tooltip>

              <Tooltip title="Visualizar PDF">
                <IconButton
                  size="small"
                  component="a"
                  href={URL.createObjectURL(file.pdf)}
                  target="_blank"
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Remover PDF">
                <IconButton size="small" onClick={() => onAttachPdf(file.id, null)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Grid>

        {canEditDueDate && hasAnyEditableDueDate && (
          <Grid item xs={12}>
            <Typography variant="caption">Duplicatas</Typography>

            {file.dup?.map((d) => {
              const editable = canEditThisDueDate(d);

              return (
                <Box display="flex" flexDirection="column">
                  <Typography noWrap>
                    Nº {d.nDup} — Valor:{" "}
                    {Number(d.vDup).toLocaleString("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    })}
                  </Typography>

                  {editable ? (
                    <Input
                      type="date"
                      fullWidth
                      inputProps={{ min: moment().add(15, 'days').format('YYYY-MM-DD') }}
                      value={d.proxydVenc}
                      onChange={(e) =>
                        onUpdateDueDate(file.id, d.nDup, e.target.value)
                      }
                    />
                  ) : (
                    <Typography sx={{ mt: 1 }}>
                      Vencimento: {d.proxydVenc}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

export const InvoiceDetail = React.memo(
  InvoiceDetailComponent,
  (prev, next) => {
    const sameFile =
      prev.file.id === next.file.id &&
      prev.file.pdf === next.file.pdf &&
      JSON.stringify(prev.file.dup) === JSON.stringify(next.file.dup);

    return (
      sameFile &&
      prev.canEditDueDate === next.canEditDueDate &&
      prev.onAttachPdf === next.onAttachPdf &&
      prev.onDelete === next.onDelete &&
      prev.onUpdateDueDate === next.onUpdateDueDate
    );
  }
);